import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Select, Table, Tag, Tooltip, Button,
} from 'antd';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import {
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserAddOutlined,
  PercentageOutlined,
  ReloadOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as api from 'util/api';
import {
  SIZE_OTHER,
  formatNumber,
  formatPercent,
  parseCanSize,
  sizeSortKey,
  CustomSpinner,
  MetricStat,
} from './shared';

const { Column } = Table;
const MONTHS_BACK = 24;

const tickCoordinates = (axis) => {
  if (!axis) return [];
  const fromTicks = (axis.ticks || [])
    .map((tick) => (typeof tick === 'number' ? tick : tick?.coordinate))
    .filter((value) => typeof value === 'number');
  if (fromTicks.length) return fromTicks.sort((a, b) => a - b);
  if (typeof axis.scale !== 'function') return [];
  const band = typeof axis.scale.bandwidth === 'function' ? axis.scale.bandwidth() / 2 : 0;
  return (axis.ticks || [])
    .map((tick) => tick?.value)
    .filter((value) => value != null)
    .map((value) => axis.scale(value) + band)
    .filter((value) => typeof value === 'number' && !Number.isNaN(value));
};

/** Pixel positions halfway between the axis ticks — the minor grid. */
const minorGrid = (axis) => {
  const points = tickCoordinates(axis);
  const mids = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    mids.push((points[i] + points[i + 1]) / 2);
  }
  return mids;
};

const formatMoney = (value) => (
  value == null ? '—' : `$${formatNumber(Math.round(value * 100) / 100)}`
);

const METRICS = [
  {
    key: 'totalCans', label: 'Charged add-to-order cans', color: '#1677ff', format: formatNumber,
  },
  {
    key: 'totalValue', label: 'Charged add-to-order value', color: '#52c41a', format: formatMoney,
  },
  {
    key: 'uniqueCustomers', label: 'Customers who added', color: '#fa8c16', format: formatNumber,
  },
  {
    key: 'addOnRate', label: 'Add-on rate', color: '#722ed1', format: formatPercent,
  },
  {
    key: 'avgValue', label: 'Avg $ / add-on customer', color: '#13c2c2', format: formatMoney,
  },
];

const METRIC_ICONS = {
  totalCans: ShoppingCartOutlined,
  totalValue: ShoppingOutlined,
  uniqueCustomers: UserAddOutlined,
  addOnRate: PercentageOutlined,
  avgValue: ShoppingOutlined,
};

const buildMonthOptions = () => {
  const current = dayjs().startOf('month');
  return Array.from({ length: MONTHS_BACK }, (_, index) => {
    const month = current.subtract(index, 'month');
    return {
      value: month.format('YYYY-MM'),
      label: index === 0
        ? `${month.format('MMMM YYYY')} (in progress)`
        : month.format('MMMM YYYY'),
    };
  });
};

const FreshClubHistory = ({ store }) => {
  const monthOptions = useMemo(buildMonthOptions, []);
  const [month, setMonth] = useState(monthOptions[1].value);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [hoveredMetric, setHoveredMetric] = useState(null);
  const [historyReload, setHistoryReload] = useState(0);
  const trendRequest = useRef(0);

  const fetchHistory = () => setHistoryReload((reload) => reload + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHistory(null);
    api.getOnetimeHistory(store, month)
      .then((data) => {
        if (cancelled) return;
        setHistory(data);
      })
      .catch(() => {
        if (cancelled) return;
        setHistory(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [store, month, historyReload]);

  useEffect(() => {
    trendRequest.current += 1;
    setTrend(null);
    setTrendLoading(false);
  }, [store]);

  const fetchTrend = () => {
    const requestId = trendRequest.current + 1;
    trendRequest.current = requestId;
    setTrendLoading(true);
    setTrend(null);
    api.getOnetimeTrend(store)
      .then((rows) => {
        if (trendRequest.current !== requestId) return;
        setTrend(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (trendRequest.current !== requestId) return;
        setTrend(null);
      })
      .finally(() => {
        if (trendRequest.current === requestId) setTrendLoading(false);
      });
  };

  const chartData = useMemo(() => {
    if (!Array.isArray(trend) || !trend.length) return [];
    const peaks = {};
    METRICS.forEach((metric) => {
      peaks[metric.key] = Math.max(0, ...trend.map((row) => Number(row[metric.key]) || 0));
    });
    return trend.map((row) => {
      const point = {
        month: row.month,
        label: dayjs(`${row.month}-01`).format('MMM YY'),
      };
      METRICS.forEach((metric) => {
        const raw = Number(row[metric.key]) || 0;
        point[metric.key] = row[metric.key];
        point[`${metric.key}Idx`] = peaks[metric.key] > 0 ? (raw / peaks[metric.key]) * 100 : 0;
      });
      return point;
    });
  }, [trend]);

  const TrendTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
      <div className="fresh-club-trend-tooltip">
        <div className="fresh-club-trend-tooltip-title">
          {dayjs(`${row.month}-01`).format('MMMM YYYY')}
        </div>
        {METRICS.map((metric) => {
          const highlighted = hoveredMetric === metric.key;
          return (
            <div
              key={metric.key}
              className={highlighted ? 'fresh-club-trend-tooltip-row--active' : undefined}
              style={{ opacity: hoveredMetric && !highlighted ? 0.4 : 1 }}
            >
              <span style={{ color: metric.color }}>● </span>
              {`${metric.label}: ${metric.format(row[metric.key])}`}
            </div>
          );
        })}
      </div>
    );
  };

  const metrics = history?.metrics;

  const productRows = useMemo(() => {
    if (!history?.products) return [];
    const totalUnits = metrics?.totalCans || 0;
    const groups = new Map();
    history.products.forEach((product) => {
      const key = product.productId || product.productTitle || 'unknown';
      const titles = product.variantTitles?.length
        ? product.variantTitles
        : [product.variantTitle];
      const sizes = titles.map((title) => parseCanSize(title, product.productTitle));
      const existing = groups.get(key);
      if (existing) {
        existing.unitCount += product.unitCount;
        existing.quantity += product.quantity;
        existing.value += product.value;
        existing.customerCount += product.customerCount;
        sizes.forEach((size) => existing.sizes.add(size));
      } else {
        groups.set(key, {
          key,
          productName: product.productTitle || 'Unknown',
          sizes: new Set(sizes),
          unitCount: product.unitCount,
          quantity: product.quantity,
          value: product.value,
          customerCount: product.customerCount,
        });
      }
    });
    return Array.from(groups.values())
      .map((row) => {
        const realSizes = Array.from(row.sizes)
          .filter((size) => size !== SIZE_OTHER)
          .sort((a, b) => sizeSortKey(a) - sizeSortKey(b));
        return {
          ...row,
          size: realSizes.length ? realSizes.join(', ') : SIZE_OTHER,
          sharePct: totalUnits > 0 ? (row.unitCount / totalUnits) * 100 : 0,
        };
      })
      .sort((a, b) => b.unitCount - a.unitCount);
  }, [history?.products, metrics?.totalCans]);

  const addOnRate = metrics?.freshClubCharges > 0
    ? (metrics.uniqueCustomers / metrics.freshClubCharges) * 100
    : null;
  const avgValue = metrics?.uniqueCustomers > 0
    ? metrics.totalValue / metrics.uniqueCustomers
    : null;
  const metricsLoading = loading || !metrics;
  const cardValues = {
    totalCans: metrics?.totalCans,
    totalValue: metrics?.totalValue,
    uniqueCustomers: metrics?.uniqueCustomers,
    addOnRate,
    avgValue,
  };
  const selectedInTrend = chartData.some((row) => row.month === month);

  return (
    <div id="fresh-club-history" className="fresh-club-panel">
      <div className="onetimes-panel-header">
        <div className="fresh-club-panel-title">Add-to-orders history</div>
        <div className="fresh-club-history-controls">
          <Tooltip title="Last 12 completed months. First load can take a couple of minutes.">
            <Button
              icon={<LineChartOutlined />}
              onClick={fetchTrend}
              loading={trendLoading}
              disabled={trendLoading}
            >
              12-month trend
            </Button>
          </Tooltip>
          <Select
            value={month}
            onChange={setMonth}
            options={monthOptions}
            disabled={loading}
            style={{ width: 220 }}
          />
          <Tooltip title="Reload month from Recharge">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchHistory}
              disabled={loading}
            />
          </Tooltip>
        </div>
      </div>

      {(trendLoading || chartData.length > 0) && (
      <div className="fresh-club-trend">
        {trendLoading && (
          <div className="fresh-club-history-loading">
            <CustomSpinner fontSize={18} />
            <span>Loading 12 months of charges. This can take a couple of minutes.</span>
          </div>
        )}
        {!trendLoading && chartData.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={chartData}
                margin={{
                  top: 8,
                  right: 8,
                  bottom: 0,
                  left: 8,
                }}
              >
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  hide
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                />
                <CartesianGrid
                  stroke="#f0f0f0"
                  strokeDasharray="3 3"
                  horizontalCoordinatesGenerator={({ yAxis }) => minorGrid(yAxis)}
                  verticalCoordinatesGenerator={({ xAxis }) => minorGrid(xAxis)}
                />
                <CartesianGrid stroke="#d9d9d9" syncWithTicks />
                <ChartTooltip content={<TrendTooltip />} />
                {selectedInTrend && (
                  <ReferenceLine
                    x={dayjs(`${month}-01`).format('MMM YY')}
                    stroke="#bfbfbf"
                  />
                )}
                {METRICS.map((metric) => {
                  const dimmed = hoveredMetric && hoveredMetric !== metric.key;
                  return (
                    <Line
                      key={metric.key}
                      type="monotone"
                      dataKey={`${metric.key}Idx`}
                      name={metric.label}
                      stroke={metric.color}
                      strokeWidth={hoveredMetric === metric.key ? 3 : 1.5}
                      strokeOpacity={dimmed ? 0.15 : 1}
                      dot={false}
                      isAnimationActive={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
            <div className="fresh-club-trend-caption">
              Each line scaled to its own 12-month peak. Hover a card to focus it.
            </div>
          </>
        )}
      </div>
      )}

      <div id="fresh-club-history-metrics">
        {METRICS.map((metric) => {
          const Icon = METRIC_ICONS[metric.key];
          const label = metric.key === 'addOnRate'
            ? `Add-on rate (of ${formatNumber(metrics?.freshClubCharges)} FRESH orders)`
            : metric.label;
          return (
            <MetricStat
              key={metric.key}
              label={label}
              icon={<Icon />}
              loading={metricsLoading}
              value={metric.format(cardValues[metric.key])}
              color={metric.color}
              active={hoveredMetric === metric.key}
              dimmed={!!hoveredMetric && hoveredMetric !== metric.key}
              onMouseEnter={() => setHoveredMetric(metric.key)}
              onMouseLeave={() => setHoveredMetric(null)}
            />
          );
        })}
      </div>

      {loading && (
        <div className="fresh-club-history-loading">
          <CustomSpinner fontSize={18} />
          <span>Loading charges from Recharge — this can take a minute...</span>
        </div>
      )}

      {!loading && history && (
        <div id="fresh-club-history-body">
          <Table
            className="fresh-club-history-table"
            dataSource={productRows}
            pagination={false}
            size="small"
            locale={{ emptyText: 'No add-to-order sales this month.' }}
          >
            <Column title="Product Name" dataIndex="productName" key="productName" />
            <Column
              title="Size"
              dataIndex="size"
              key="size"
              width={100}
              sorter={(a, b) => sizeSortKey(a.size) - sizeSortKey(b.size)}
              render={(val) => (
                <Tag color={val === SIZE_OTHER ? 'default' : 'geekblue'}>{val}</Tag>
              )}
            />
            <Column
              title="Cans"
              dataIndex="unitCount"
              key="unitCount"
              width={110}
              align="right"
              sorter={(a, b) => a.unitCount - b.unitCount}
              render={(val, row) => (val > 0
                ? formatNumber(val)
                : `${formatNumber(row.quantity)} ${row.quantity === 1 ? 'item' : 'items'}`)}
            />
            <Column
              title="Value"
              dataIndex="value"
              key="value"
              width={110}
              align="right"
              sorter={(a, b) => a.value - b.value}
              render={formatMoney}
            />
            <Column
              title="Customers"
              dataIndex="customerCount"
              key="customerCount"
              width={100}
              align="right"
              sorter={(a, b) => a.customerCount - b.customerCount}
            />
            <Column
              title="Share"
              dataIndex="sharePct"
              key="sharePct"
              width={90}
              align="right"
              render={formatPercent}
            />
          </Table>

          <div className="fresh-club-history-dates">
            <div className="sale-mix-section-label">Charge dates</div>
            {history.chargeDates.length === 0 ? (
              <p className="sale-mix-empty">No add-to-order charges.</p>
            ) : (
              <div className="sale-mix-charge-list">
                {history.chargeDates.map((bucket) => (
                  <div className="sale-mix-charge-row" key={bucket.date}>
                    <span className="sale-mix-charge-date">
                      {dayjs(bucket.date).format('D MMM YYYY')}
                    </span>
                    <span className="sale-mix-charge-count">
                      {`${formatNumber(bucket.units)} cans`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FreshClubHistory;
